import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabaseClient'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const formatDate = (iso) => {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch {
    return '—'
  }
}

const RoleBadge = ({ isOwner }) =>
  isOwner ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 ring-1 ring-amber-300">
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19h14M6 7l3 4 3-6 3 6 3-4-1 12H7L6 7z" />
      </svg>
      Owner
    </span>
  ) : (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 ring-1 ring-blue-200">
      Admin
    </span>
  )

const AdminAdmins = () => {
  const navigate = useNavigate()
  const { user, isOwner, adminChecked, isAdmin, updatePassword } = useAuth()

  const [admins, setAdmins] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [toast, setToast] = useState(null)

  // Grant modal
  const [grantOpen, setGrantOpen] = useState(false)
  const [grantEmail, setGrantEmail] = useState('')
  const [grantReason, setGrantReason] = useState('')
  const [grantBusy, setGrantBusy] = useState(false)
  const [grantErr, setGrantErr] = useState('')

  // Revoke modal
  const [revokeTarget, setRevokeTarget] = useState(null)
  const [revokeReason, setRevokeReason] = useState('')
  const [revokeBusy, setRevokeBusy] = useState(false)
  const [revokeErr, setRevokeErr] = useState('')

  // Password change
  const [pwOpen, setPwOpen] = useState(false)
  const [pwNew, setPwNew] = useState('')
  const [pwConfirm, setPwConfirm] = useState('')
  const [pwBusy, setPwBusy] = useState(false)
  const [pwErr, setPwErr] = useState('')

  const loadAdmins = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) {
        setError('Autentifikácia zlyhala')
        return
      }
      const resp = await fetch(`${BACKEND_URL}/api/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) {
        if (resp.status === 403) {
          setError('Iba owner môže zobraziť správu adminov.')
        } else {
          setError(`HTTP ${resp.status}`)
        }
        return
      }
      const data = await resp.json()
      setAdmins(data.admins || [])
    } catch (err) {
      console.error('[AdminAdmins] load error:', err)
      setError('Nepodarilo sa načítať adminov.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!user || !adminChecked || !isOwner) return
    loadAdmins()
  }, [user, adminChecked, isOwner, loadAdmins])

  useEffect(() => {
    if (!toast) return undefined
    const t = setTimeout(() => setToast(null), 5000)
    return () => clearTimeout(t)
  }, [toast])

  // ---- Submit grant ----
  const submitGrant = async () => {
    setGrantErr('')
    const email = grantEmail.trim().toLowerCase()
    if (!email || !email.includes('@')) {
      setGrantErr('Zadajte platný e-mail.')
      return
    }
    try {
      setGrantBusy(true)
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      const resp = await fetch(`${BACKEND_URL}/api/admin/admins/grant`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, reason: grantReason.trim() }),
      })
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`
        try { msg = (await resp.json()).detail || msg } catch { /* noop */ }
        setGrantErr(msg)
        return
      }
      const result = await resp.json()
      setToast({ type: 'success', message: `Admin práva udelené pre ${result.email}.` })
      setGrantOpen(false)
      setGrantEmail('')
      setGrantReason('')
      loadAdmins()
    } catch (err) {
      console.error('[AdminAdmins] grant error:', err)
      setGrantErr('Nepodarilo sa udeliť admin práva.')
    } finally {
      setGrantBusy(false)
    }
  }

  // ---- Submit revoke ----
  const submitRevoke = async () => {
    if (!revokeTarget) return
    setRevokeErr('')
    try {
      setRevokeBusy(true)
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      const resp = await fetch(`${BACKEND_URL}/api/admin/admins/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: revokeTarget.id,
          reason: revokeReason.trim(),
        }),
      })
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`
        try { msg = (await resp.json()).detail || msg } catch { /* noop */ }
        setRevokeErr(msg)
        return
      }
      const result = await resp.json()
      setToast({ type: 'success', message: `Admin práva odobraté pre ${result.email}.` })
      setRevokeTarget(null)
      setRevokeReason('')
      loadAdmins()
    } catch (err) {
      console.error('[AdminAdmins] revoke error:', err)
      setRevokeErr('Nepodarilo sa odobrať admin práva.')
    } finally {
      setRevokeBusy(false)
    }
  }

  // ---- Submit password change ----
  const submitPasswordChange = async () => {
    setPwErr('')
    if (!pwNew || pwNew.length < 8) {
      setPwErr('Heslo musí mať aspoň 8 znakov.')
      return
    }
    if (pwNew !== pwConfirm) {
      setPwErr('Heslá sa nezhodujú.')
      return
    }
    try {
      setPwBusy(true)
      const result = await updatePassword(pwNew)
      if (result?.error) {
        setPwErr(result.error.message || 'Nepodarilo sa zmeniť heslo.')
        return
      }
      setToast({ type: 'success', message: 'Heslo bolo úspešne zmenené.' })
      setPwOpen(false)
      setPwNew('')
      setPwConfirm('')
    } catch (err) {
      console.error('[AdminAdmins] password change error:', err)
      setPwErr('Nepodarilo sa zmeniť heslo.')
    } finally {
      setPwBusy(false)
    }
  }

  // ----- Access checks -----
  if (!adminChecked) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Overujem prístup…</p>
        </div>
      </div>
    )
  }

  if (!isAdmin || !isOwner) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 ring-1 ring-slate-100 shadow-sm p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Prístup zamietnutý</h2>
          <p className="text-sm text-gray-600 mb-4">
            Túto sekciu môže otvoriť iba owner / super-admin.
          </p>
          <button
            onClick={() => navigate('/admin')}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition"
          >
            Späť do Admin Panelu
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-transparent">
      <div className="container mx-auto px-4 py-8 md:py-10">

        {/* Intro */}
        <section className="bg-gradient-to-br from-white via-blue-50/30 to-amber-50/30 border border-slate-200 ring-1 ring-slate-100 rounded-2xl shadow-md shadow-slate-200/40 p-6 sm:p-8 mb-6 md:mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <button
                onClick={() => navigate('/admin')}
                className="group inline-flex items-center gap-1.5 mb-3 px-3 py-1.5 text-sm font-semibold text-blue-700 bg-white ring-1 ring-blue-200 hover:bg-blue-50 hover:ring-blue-300 rounded-full shadow-sm transition-all duration-200"
              >
                <svg className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Admin Panel
              </button>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Správa adminov
              </h1>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl">
                Owner / super-admin môže udeliť alebo odobrať admin práva existujúcim používateľom.
                Owner je riadený cez environment a nemôže byť degradovaný cez API.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setPwOpen(true); setPwErr('') }}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-700 bg-white ring-1 ring-slate-200 hover:bg-slate-50 transition"
                data-testid="open-password-modal"
              >
                Zmeniť heslo
              </button>
              <button
                onClick={() => { setGrantOpen(true); setGrantErr('') }}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-gradient-to-b from-blue-600 to-indigo-600 shadow-sm shadow-blue-500/25 ring-1 ring-blue-700/30 hover:from-blue-700 hover:to-indigo-700 transition inline-flex items-center gap-1.5"
                data-testid="open-grant-admin-modal"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Udeliť admin
              </button>
            </div>
          </div>
        </section>

        {error && (
          <div className="mb-6 p-4 bg-rose-50 ring-1 ring-rose-200 rounded-2xl">
            <p className="text-rose-700 text-sm">{error}</p>
          </div>
        )}

        <section className="bg-white border border-slate-100 ring-1 ring-slate-50 rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
              <p className="text-gray-600 text-sm">Načítavam…</p>
            </div>
          ) : admins.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-600">Zatiaľ nie sú žiadni admini.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="text-left font-semibold px-5 py-3">E-mail</th>
                    <th className="text-left font-semibold px-5 py-3">Rola</th>
                    <th className="text-left font-semibold px-5 py-3">Vytvorené</th>
                    <th className="text-left font-semibold px-5 py-3">Naposledy zmenené</th>
                    <th className="text-right font-semibold px-5 py-3">Akcie</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {admins.map((a) => {
                    const isSelf = user && a.id === user.id
                    const canRevoke = !isSelf && !a.is_owner
                    return (
                      <tr key={a.id} className="hover:bg-slate-50/50 transition" data-testid={`admin-row-${a.id}`}>
                        <td className="px-5 py-3">
                          <div className="font-medium text-gray-900 break-all">{a.email}</div>
                          {a.display_name && (
                            <div className="text-xs text-gray-500">{a.display_name}</div>
                          )}
                          {isSelf && (
                            <span className="inline-flex mt-1 items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                              Vy
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3"><RoleBadge isOwner={a.is_owner} /></td>
                        <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{formatDate(a.created_at)}</td>
                        <td className="px-5 py-3 text-gray-600 whitespace-nowrap">{formatDate(a.updated_at)}</td>
                        <td className="px-5 py-3 text-right">
                          {canRevoke ? (
                            <button
                              onClick={() => { setRevokeTarget(a); setRevokeReason(''); setRevokeErr('') }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-rose-700 bg-rose-50 ring-1 ring-rose-200 hover:bg-rose-100 hover:ring-rose-300 transition"
                              data-testid={`revoke-admin-${a.id}`}
                            >
                              Odobrať
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400" title={isSelf ? 'Nemôžete meniť vlastnú rolu' : 'Owner sa nedá odobrať cez API'}>
                              {isSelf ? '—' : 'chránený'}
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 max-w-md px-4 py-3 rounded-2xl shadow-lg ring-1 ${
            toast.type === 'success'
              ? 'bg-emerald-50 ring-emerald-200 text-emerald-800'
              : 'bg-rose-50 ring-rose-200 text-rose-800'
          }`}
          role="status"
          data-testid="admin-admins-toast"
        >
          <div className="flex items-start gap-2">
            <p className="text-sm leading-snug flex-1">{toast.message}</p>
            <button onClick={() => setToast(null)} aria-label="Zatvoriť" className="opacity-60 hover:opacity-100">✕</button>
          </div>
        </div>
      )}

      {/* Grant modal */}
      {grantOpen && (
        <Modal title="Udeliť admin práva" onClose={() => !grantBusy && setGrantOpen(false)}>
          <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3 text-xs text-amber-900 leading-snug">
            Promote existujúceho používateľa na admina. Owner status (super-admin) sa cez API udelť nedá — riadi ho environment.
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1.5" htmlFor="grant-email">E-mail používateľa</label>
            <input
              id="grant-email"
              type="email"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              placeholder="napr. ucitel@skola.sk"
              className="w-full px-3 py-2 rounded-xl ring-1 ring-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="grant-admin-email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1.5" htmlFor="grant-reason">Dôvod (interná poznámka)</label>
            <textarea
              id="grant-reason"
              value={grantReason}
              onChange={(e) => setGrantReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Napr.: Druhý admin pre podporu / školský rok 2025/2026"
              className="w-full px-3 py-2 rounded-xl ring-1 ring-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
              data-testid="grant-admin-reason"
            />
          </div>
          {grantErr && (
            <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 px-4 py-2.5 text-sm text-rose-800">{grantErr}</div>
          )}
          <ModalFooter onCancel={() => setGrantOpen(false)} cancelDisabled={grantBusy} onConfirm={submitGrant} confirmDisabled={grantBusy} confirmLabel={grantBusy ? 'Pridávam…' : 'Udeliť admin'} confirmTestId="grant-admin-confirm" />
        </Modal>
      )}

      {/* Revoke modal */}
      {revokeTarget && (
        <Modal title="Odobrať admin práva" onClose={() => !revokeBusy && setRevokeTarget(null)}>
          <p className="text-sm text-gray-700">
            Naozaj chcete odobrať admin práva používateľovi <strong className="break-all">{revokeTarget.email}</strong>?
            Stratí prístup do admin panela.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1.5" htmlFor="revoke-reason">Dôvod (interná poznámka)</label>
            <textarea
              id="revoke-reason"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 rounded-xl ring-1 ring-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
              data-testid="revoke-admin-reason"
            />
          </div>
          {revokeErr && (
            <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 px-4 py-2.5 text-sm text-rose-800">{revokeErr}</div>
          )}
          <ModalFooter onCancel={() => setRevokeTarget(null)} cancelDisabled={revokeBusy} onConfirm={submitRevoke} confirmDisabled={revokeBusy} confirmLabel={revokeBusy ? 'Odoberám…' : 'Potvrdiť odobratie'} confirmTone="rose" confirmTestId="revoke-admin-confirm" />
        </Modal>
      )}

      {/* Password change */}
      {pwOpen && (
        <Modal title="Zmena hesla" onClose={() => !pwBusy && setPwOpen(false)}>
          <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3 text-xs text-amber-900 leading-snug">
            Heslo je riadené cez Supabase Auth. Po zmene zostávate prihlásený.
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1.5" htmlFor="pw-new">Nové heslo</label>
            <input id="pw-new" type="password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} placeholder="Min. 8 znakov" className="w-full px-3 py-2 rounded-xl ring-1 ring-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="pw-new" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-800 mb-1.5" htmlFor="pw-confirm">Potvrďte heslo</label>
            <input id="pw-confirm" type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} className="w-full px-3 py-2 rounded-xl ring-1 ring-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" data-testid="pw-confirm" />
          </div>
          {pwErr && (
            <div className="rounded-xl bg-rose-50 ring-1 ring-rose-200 px-4 py-2.5 text-sm text-rose-800">{pwErr}</div>
          )}
          <ModalFooter onCancel={() => setPwOpen(false)} cancelDisabled={pwBusy} onConfirm={submitPasswordChange} confirmDisabled={pwBusy} confirmLabel={pwBusy ? 'Mením…' : 'Zmeniť heslo'} confirmTestId="pw-confirm-btn" />
        </Modal>
      )}
    </div>
  )
}

// ----- Reusable Modal & Footer -----
const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose}>
    <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
      <div className="bg-gradient-to-br from-blue-50 via-white to-emerald-50/40 px-6 py-4 border-b border-slate-100">
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      </div>
      <div className="px-6 py-5 space-y-4">{children}</div>
    </div>
  </div>
)

const ModalFooter = ({ onCancel, cancelDisabled, onConfirm, confirmDisabled, confirmLabel, confirmTone, confirmTestId }) => {
  const tone = confirmTone === 'rose'
    ? 'bg-gradient-to-b from-rose-600 to-rose-700 ring-rose-700/30 hover:from-rose-700 hover:to-rose-800 shadow-rose-500/25'
    : 'bg-gradient-to-b from-blue-600 to-indigo-600 ring-blue-700/30 hover:from-blue-700 hover:to-indigo-700 shadow-blue-500/25'
  return (
    <div className="-mx-6 -mb-5 mt-2 px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
      <button onClick={onCancel} disabled={cancelDisabled} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-700 bg-white ring-1 ring-slate-200 hover:bg-slate-100 disabled:opacity-60">Zrušiť</button>
      <button onClick={onConfirm} disabled={confirmDisabled} className={`px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-sm ring-1 disabled:opacity-60 ${tone}`} data-testid={confirmTestId}>{confirmLabel}</button>
    </div>
  )
}

export default AdminAdmins
